import { englishError } from "../utils/databaseErrors";
// src/components/BlockForm.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";
import { businessDate, PRODUCT_TYPES, stockCountLabel } from "../utils/stock";
import { sortMaterials } from "../utils/materialNames";
import { productSnapshot, stockSnapshot, stockFinish } from "../utils/productStock";
import { sumQuantities, validMarbleQuantity } from "../utils/marbleQuantity";
import { findBlockDuplicates, findMaterialDuplicates, readDuplicateMaterials } from "../utils/duplicates";
import DuplicateWarning from "./DuplicateWarning";

async function allRows(table, filter = query => query) {
  const rows = [];
  for (let start = 0; ; start += 1000) {
    const { data, error } = await filter(supabase.from(table).select("*").order("id").range(start,start+999));
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < 1000) return rows;
  }
}

/* ---------------- Popup: إضافة عميل/مورد ---------------- */
function SmallAddPopup({ onSaved, onClose }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    roles: ["مورد"],
  });

  function toggleRole(role) {
    setForm((f) =>
      f.roles.includes(role)
        ? { ...f, roles: f.roles.filter((r) => r !== role) }
        : { ...f, roles: [...f.roles, role] }
    );
  }

  async function handleAdd() {
    if (!form.name) return alert("Customer name/Supplier is required");
    const phone = (form.phone || "").trim();
    if (phone) {
      const { data: existing, error: checkError } = await supabase
        .from("customer")
        .select("id")
        .eq("phone", phone)
        .limit(1);
      if (checkError) {
        return alert("Unable to check the phone number: " + englishError(checkError.message));
      }
      if (existing && existing.length) {
        return alert("This phone number belongs to another customer.");
      }
    }

    const payload = { ...form, phone };
    const { data, error } = await supabase.from("customer").insert([payload]).select();
    if (error) return alert("Unable to save: " + englishError(error.message));
    if (onSaved && data && data[0]) onSaved(data[0].id);
    onClose();
  }

  return (
    <div className="ui-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "#51453610",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 5000,
      }}
    >
      <div className="ui-modal ui-modal-small"
        style={{
          background: "#faf8f4",
          padding: 28,
          borderRadius: 12,
          minWidth: 300,
          textAlign: "left",
          direction: "ltr",
        }}
      >
        <h4>Add customer/New supplier</h4>
        <input
          placeholder="Customer name/Supplier"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          style={{ width: "100%", marginBottom: 10, padding: 6 }}
        />
        <input
          placeholder="Phone number"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          style={{ width: "100%", marginBottom: 10, padding: 6 }}
        />
        <input
          placeholder="Email address"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          style={{ width: "100%", marginBottom: 10, padding: 6 }}
        />
        <div style={{ marginBottom: 14 }}>
          <label>
            <input
              type="checkbox"
              checked={form.roles.includes("مورد")}
              onChange={() => toggleRole("مورد")}
            />{" "}
            Supplier
          </label>
          <label style={{ marginRight: 14 }}>
            <input
              type="checkbox"
              checked={form.roles.includes("عميل")}
              onChange={() => toggleRole("عميل")}
            />{" "}
            Customer
          </label>
        </div>
        <div className="ui-actions" style={{ display: "flex", gap: 8 }}>
          <button className="ui-secondary" onClick={handleAdd}>Save</button>
          <button className="ui-secondary" onClick={onClose} style={{ background: "#d2cbc1" }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Popup: إضافة خامة ---------------- */
function MaterialAddPopup({ onSaved, onClose }) {
  const [form, setForm] = useState({ name: "", country: "", color: "" });
  const [duplicates, setDuplicates] = useState(null), [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  async function handleAdd(allowDuplicate = false) {
    if (savingRef.current) return;
    if (!form.name.trim()) { setError("Material name is required"); return; }
    savingRef.current = true; setSaving(true); setError("");
    try {
      if (!allowDuplicate) {
        const matches = findMaterialDuplicates(form.name, await readDuplicateMaterials(supabase));
        if (matches.length) { setDuplicates(matches); return; }
      }
      const payload = Object.fromEntries(Object.entries(form).map(([key,value]) => [key,value.trim()]));
      const { data, error: saveError } = await supabase.from("material").insert([payload]).select();
      if (saveError) throw saveError;
      if (onSaved && data && data[0]) onSaved(data[0].id);
      onClose();
    } catch (err) { setError("Unable to save material: " + englishError(err.message)); setDuplicates(null); }
    finally { savingRef.current = false; setSaving(false); }
  }

  return (
    <div className="ui-backdrop"
      style={{
        position: "fixed",
        inset: 0,
        background: "#51453610",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 5000,
      }}
    >
      <div className="ui-modal ui-modal-small"
        style={{
          background: "#faf8f4",
          padding: 28,
          borderRadius: 12,
          minWidth: 320,
          textAlign: "left",
          direction: "ltr",
        }}
      >
        <h4>Add material</h4>
        {error && <p role="alert">{error}</p>}
        <input
          placeholder="Material name"
          disabled={saving || !!duplicates}
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          style={{ width: "100%", marginBottom: 10, padding: 6 }}
        />
        <input
          placeholder="Country"
          disabled={saving || !!duplicates}
          value={form.country}
          onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
          style={{ width: "100%", marginBottom: 10, padding: 6 }}
        />
        <input
          placeholder="Color"
          disabled={saving || !!duplicates}
          value={form.color}
          onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
          style={{ width: "100%", marginBottom: 10, padding: 6 }}
        />
        <div className="ui-actions" style={{ display: "flex", gap: 8 }}>
          <button type="button" className="ui-secondary" disabled={saving} onClick={() => handleAdd()}>Save</button>
          <button type="button" className="ui-secondary" disabled={saving} onClick={onClose} style={{ background: "#d2cbc1" }}>
            Cancel
          </button>
        </div>
        {duplicates && <DuplicateWarning kind="material" matches={duplicates} busy={saving}
          onCancel={() => setDuplicates(null)} onConfirm={() => handleAdd(true)} />}
      </div>
    </div>
  );
}

/* -------------------- BlockForm -------------------- */
function BlockForm({ initialBlock = null, user, onSaved, onClose, onBusyChange }) {
  const isEdit = !!initialBlock?.id;
  const [distribute, setDistribute] = useState(false);
  const [originalStock, setOriginalStock] = useState(null);
  const [originalProduct, setOriginalProduct] = useState(initialBlock);
  const [defaultShowroom, setDefaultShowroom] = useState(String(user?.showroom_id || ""));
  const [loadingOptions, setLoadingOptions] = useState(true), [loadingStock, setLoadingStock] = useState(true);
  const [loadError, setLoadError] = useState(""), [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [duplicateSave, setDuplicateSave] = useState(null);
  useEffect(() => { onBusyChange?.(saving); }, [saving, onBusyChange]);
  const [form, setForm] = useState(() => ({
    code: initialBlock?.code ?? "",
    entry_date: initialBlock ? initialBlock.entry_date ?? "" : businessDate(),
    product_type: initialBlock?.product_type ?? "slab",
    material_id: initialBlock?.material_id ?? "",
    customer_id: initialBlock?.customer_id ?? "",
    width: initialBlock?.width ?? "",
    height: initialBlock?.height ?? "",
    thickness: initialBlock?.thickness ?? "",
    slabs_number: initialBlock?.slabs_number ?? "",
    m_price: initialBlock?.m_price ?? "",
    sale_price_per_m2: initialBlock?.sale_price_per_m2 ?? "",
    notes_text: initialBlock?.notes_text ?? "",
  }));

  const [customers, setCustomers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [showroomDistributions, setShowroomDistributions] = useState([]);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);

  // ------- معالجة (وجه/ظهر/لمعة) -------
  const coalesceTreatment = (blk) => {
    try {
      if (
        blk?.face_treatment ||
        blk?.back_treatment ||
        typeof blk?.is_polished === "boolean"
      ) {
        return {
          face: blk.face_treatment || "none",
          back: blk.back_treatment || "none",
          polished: !!blk.is_polished,
        };
      }
      if (blk?.notes_text) {
        const j = JSON.parse(blk.notes_text);
        return {
          face: j.face || "none",
          back: j.back || "none",
          polished: !!j.polished,
        };
      }
    } catch {}
    return { face: "none", back: "none", polished: false };
  };

  const [treatment, setTreatment] = useState(() => coalesceTreatment(initialBlock));

  useEffect(() => {
    setTreatment(coalesceTreatment(initialBlock));
    setForm((f) => ({
      ...f,
      code: initialBlock?.code ?? "",
      entry_date: initialBlock ? initialBlock.entry_date ?? "" : businessDate(),
      product_type: initialBlock?.product_type ?? "slab",
      material_id: initialBlock?.material_id ?? "",
      customer_id: initialBlock?.customer_id ?? "",
      width: initialBlock?.width ?? "",
      height: initialBlock?.height ?? "",
      thickness: initialBlock?.thickness ?? "",
      slabs_number: initialBlock?.slabs_number ?? "",
      m_price: initialBlock?.m_price ?? "",
      sale_price_per_m2: initialBlock?.sale_price_per_m2 ?? "",
      notes_text: initialBlock?.notes_text ?? "",
    }));
  }, [initialBlock]);

  const fetchAll = useCallback(async () => {
    setLoadingOptions(true); setLoadError("");
    try {
      const [custs,mats,shrs] = await Promise.all([allRows("customer"),allRows("material"),allRows("showroom")]);
      setCustomers(custs); setMaterials(sortMaterials(mats)); setShowrooms(shrs);
    } catch (err) { setLoadError("Unable to load product options: " + englishError(err.message)); }
    finally { setLoadingOptions(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    let cancelled = false;
    setLoadingStock(true); setOriginalStock(null); setSaveError(""); setDistribute(false);
    (async () => {
      try {
        const [products, rows] = initialBlock?.id ? await Promise.all([
          allRows("block", query => query.eq("id",initialBlock.id)),
          allRows("block_distribution", query => query.eq("block_id",initialBlock.id)),
        ]) : [[],[]];
        if (cancelled) return;
        const fresh = products[0];
        if (initialBlock?.id && !fresh) throw new Error("The product no longer exists. Refresh the list.");
        setOriginalProduct(fresh || null);
        setOriginalStock(stockSnapshot(rows)); setShowroomDistributions(rows);
        if (fresh) {
          setForm(Object.fromEntries(["code","entry_date","material_id","customer_id","width","height","thickness","m_price","sale_price_per_m2","notes_text"].map(key => [key,fresh[key] ?? ""])));
          setForm(current => ({...current,product_type:fresh.product_type || "slab",slabs_number:rows.length ? sumQuantities(rows.map(row => row.slabs_count)) : fresh.slabs_number ?? 0}));
          setTreatment(coalesceTreatment(fresh));
        }
        if (fresh && rows.length) {
          if (new Set(rows.map(stockFinish)).size === 1) setTreatment({face:rows[0].face_treatment || "none",back:rows[0].back_treatment || "none",polished:!!rows[0].is_polished});
        }
        setDistribute(rows.length > 1);
      } catch (err) { if (!cancelled) setSaveError("Unable to load showroom stock: " + englishError(err.message)); }
      finally { if (!cancelled) setLoadingStock(false); }
    })();
    return () => { cancelled = true; };
  }, [initialBlock]);
  const mixedTreatments = new Set((originalStock || []).map(stockFinish)).size > 1;
  const quantity = isEdit && distribute ? sumQuantities(showroomDistributions.map(row => row.slabs_count)) : form.slabs_number;

  const total_area =
    Number(form.width) > 0 &&
    Number(form.height) > 0 &&
    Number(quantity) > 0
      ? (
          Number(form.width) *
          Number(form.height) *
          Number(quantity)
        ).toFixed(2)
      : "";

  function handleShowroomChange(idx, field, value) {
    setShowroomDistributions((arr) =>
      arr.map((item, i) => (i === idx ? { ...item, [field]: value } : item))
    );
  }
  function addShowroomDistribution() {
    setShowroomDistributions((arr) => [
      ...arr,
      { showroom_id: "", slabs_count: "" },
    ]);
  }
  function removeShowroomDistribution(idx) {
    setShowroomDistributions((arr) => arr.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e, allowDuplicate = false) {
    e.preventDefault();
    if (savingRef.current || loadingOptions || loadingStock || loadError || originalStock === null) return;
    setSaveError("");
    const slabsNum = Number(quantity);
    const price = value => value === "" || value == null ? null : Number(value);
    const payload = {
      code: isEdit ? form.code.trim() : null, product_type: form.product_type,
      material_id: Number(form.material_id), customer_id: Number(form.customer_id),
      width: Number(form.width), height: Number(form.height), thickness: Number(form.thickness || 0),
      slabs_number: slabsNum, m_price: price(form.m_price), sale_price_per_m2: price(form.sale_price_per_m2),
      face_treatment: treatment.face, back_treatment: treatment.back, is_polished: treatment.polished,
      notes_text: form.notes_text,
      entry_date: form.entry_date || null,
    };
    if ((!isEdit && !form.entry_date) || (form.entry_date &&
        (!/^\d{4}-\d{2}-\d{2}$/.test(form.entry_date) || !Number.isFinite(Date.parse(form.entry_date)) ||
         new Date(form.entry_date).toISOString().slice(0,10) !== form.entry_date))) {
      setSaveError("Enter a valid entry date."); return;
    }
    if ((isEdit && !payload.code) || !form.material_id || !form.customer_id ||
        !Number.isFinite(payload.width) || payload.width <= 0 || !Number.isFinite(payload.height) || payload.height <= 0 ||
        !validMarbleQuantity(slabsNum,isEdit) ||
        !Number.isFinite(payload.thickness) || payload.thickness < 0 ||
        [payload.m_price,payload.sale_price_per_m2].some(value => value !== null && (!Number.isFinite(value) || value < 0))) {
      setSaveError("Complete the material, supplier and dimensions, and enter valid stock quantities and optional prices."); return;
    }
    let distributions = null;
    if (distribute) {
      if (!showroomDistributions.length || showroomDistributions.some(row => !row.showroom_id || row.slabs_count === "" ||
          !validMarbleQuantity(row.slabs_count,!!row.id))) {
        setSaveError("Select a showroom and enter a quantity with up to three decimal places for each stock row."); return;
      }
      distributions = showroomDistributions.map(row => ({ id:row.id || null,showroom_id:Number(row.showroom_id),slabs_count:Number(row.slabs_count) }));
      if (sumQuantities(distributions.map(row => row.slabs_count)) !== slabsNum) { setSaveError("The showroom quantities must equal the total product quantity."); return; }
    } else if ((!isEdit || !originalStock.length) && !user?.showroom_id && slabsNum > 0) {
      if (!defaultShowroom) { setSaveError("Select a stock showroom or assign a showroom to this employee."); return; }
      distributions = [{showroom_id:Number(defaultShowroom),slabs_count:slabsNum}];
    }
    if (!user?.id) { setSaveError("Sign in again before saving this product."); return; }
    savingRef.current = true; setSaving(true);
    try {
      if (!allowDuplicate) {
        const matches = await findBlockDuplicates(supabase, payload, isEdit ? initialBlock.id : null);
        if (matches.length) { setDuplicateSave(matches); return; }
      }
      const { data, error } = await supabase.rpc("save_marble_product_tx", {
        p_employee_id:user.id,p_block_id:isEdit ? initialBlock.id : null,p_product:payload,p_distributions:distributions,
        p_expected_product:isEdit ? productSnapshot(originalProduct) : null,p_expected_stock:isEdit ? originalStock : null,
      });
      if (error) throw error;
      if (!data) throw new Error("The product was not saved. Refresh the list and try again.");
      setDuplicateSave(null);
      onSaved?.(); onClose?.();
    } catch (err) { setSaveError("Unable to save the product: " + englishError(err.message)); setDuplicateSave(null); }
    finally { savingRef.current = false; setSaving(false); }
  }

  return (
    <div className="ui-legacy-page" dir="ltr" style={{ maxWidth: 720, margin: "auto", padding: 18 }}>
      {/* CSS خفيف للصفحة دي فقط */}
      <style>{`
      .bf-card{background:#faf8f4;border-radius:12px;box-shadow:0 1px 10px #51453610;padding:16px;}
      .bf-title{color:var(--accent, #514536);margin:0 0 12px 0}
      .bf{--lbl:130px;--gap:10px;border:0;padding:0;margin:0;min-inline-size:0}
      .bf-row{display:grid;grid-template-columns:var(--lbl) 1fr;align-items:center;column-gap:var(--gap);margin-bottom:10px}
      .bf-row-3{grid-template-columns:var(--lbl) 1fr auto}
      .bf-input{height:36px;padding:6px 10px;border:1px solid var(--line, #d2cbc1);border-radius:8px;background:#faf8f4;font-size:14px}
      .bf-input[readonly]{background:#f2ede4;color:var(--accent, #514536);font-weight:700}
      .bf-mini{height:36px;padding:0 10px;border:1px solid #d2cbc1;border-radius:8px;background:#f2ede4;color:var(--accent, #514536);font-weight:700;cursor:pointer}
      .bf-grid-3{display:grid;grid-template-columns:repeat(3,1fr);gap:var(--gap)}
      .bf-grid-2{display:grid;grid-template-columns:repeat(2,1fr);gap:var(--gap)}
      .bf-sec{margin-top:12px;padding:12px;border:1px solid #d2cbc1;border-radius:10px;background:#faf8f4}
      .bf-sec h4{margin:0 0 8px 0;color:var(--accent, #514536)}
      .bf-radio label{margin-left:14px;white-space:nowrap}
      .bf-dist-row{display:grid;grid-template-columns:1fr 140px auto;gap:var(--gap);margin-bottom:8px}
      .bf-dist-row>button{align-self:end}
      .bf-save{width:100%;padding:11px;font-weight:700;background:var(--accent, #514536);color:#faf8f4;border-radius:8px;border:none;font-size:16px}
      .bf-warn{color:#716759;font-size:12px}
    `}</style>

      {duplicateSave && <DuplicateWarning kind="block" matches={duplicateSave} busy={saving}
        onCancel={() => setDuplicateSave(null)} onConfirm={() => handleSubmit({preventDefault() {}}, true)} />}
      <h2 className="bf-title">
        {initialBlock ? "Edit product" : "Add product"}
      </h2>
      <div className="bf-card">
        {(loadingOptions || loadingStock) && <p role="status">Loading product details…</p>}
        {loadError && <p role="alert">{loadError}</p>}
        {saveError && <p role="alert">{saveError}</p>}
        <form className="ui-form" onSubmit={handleSubmit}>
          <fieldset className="bf" disabled={saving || loadingOptions || loadingStock || !!loadError || originalStock === null}>
          <div className="bf-row ui-field">
            <label htmlFor="sale-price">Selling price / m² (optional):</label>
            <input id="sale-price" className="bf-input" type="number" min="0" step="0.01"
              value={form.sale_price_per_m2} onChange={(event) => setForm((current) => ({ ...current, sale_price_per_m2: event.target.value }))} />
          </div>
          <div className="bf-row ui-field">
            <label htmlFor="product-type">Product type:</label>
            <select id="product-type" className="bf-input" value={form.product_type}
              onChange={(event) => setForm((current) => ({ ...current, product_type: event.target.value }))}>
              {Object.entries(PRODUCT_TYPES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          {/* الكود */}
          <div className="bf-row ui-field">
            <label htmlFor="block-entry-date">Entry date:</label>
            <input id="block-entry-date" className="bf-input" type="date" required={!isEdit}
              value={form.entry_date}
              onChange={(event) => setForm((current) => ({ ...current, entry_date: event.target.value }))} />
          </div>
          <div className="bf-row ui-field">
            <label htmlFor="product-code">Code:</label>
            <input
              id="product-code"
              className="bf-input"
              value={form.code}
              readOnly={!initialBlock?.id}
              placeholder={!initialBlock?.id ? "Generated on save" : undefined}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              required={!!initialBlock?.id}
            />
          </div>

          {/* الخامة */}
          <div className="bf-row-3 ui-field">
            <label htmlFor="block-material">Material:</label>
            <select
              className="bf-input"
              id="block-material"
              value={form.material_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, material_id: e.target.value }))
              }
              required
            >
              <option value="">Select a material...</option>
              {materials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name || m.material_name || m.id}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="bf-mini ui-secondary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAddMaterial(true);
              }}
            >
              + New
            </button>
          </div>

          {/* المورد */}
          <div className="bf-row-3 ui-field">
            <label htmlFor="block-supplier">Supplier:</label>
            <select
              className="bf-input"
              id="block-supplier"
              value={form.customer_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, customer_id: e.target.value }))
              }
              required
            >
              <option value="">Select a supplier...</option>
              {customers.filter(c => c.roles?.includes("مورد") || c.roles?.includes("supplier") || String(c.id) === String(form.customer_id)).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="bf-mini ui-secondary"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowAddCustomer(true);
              }}
            >
              + New
            </button>
          </div>

          {/* المقاسات */}
          <div className="bf-row">
            <label>Size:</label>
            <div className="bf-grid-3">
              <input
                className="bf-input"
                type="number"
                min={0}
                step="0.01"
                placeholder="Width (m)"
                value={form.width}
                onChange={(e) =>
                  setForm((f) => ({ ...f, width: e.target.value }))
                }
                required
              />
              <input
                className="bf-input"
                type="number"
                min={0}
                step="0.01"
                placeholder="Height (m)"
                value={form.height}
                onChange={(e) =>
                  setForm((f) => ({ ...f, height: e.target.value }))
                }
                required
              />
              <input
                className="bf-input"
                type="number"
                min={0}
                step="0.01"
                placeholder="Thickness (cm)"
                value={form.thickness}
                onChange={(e) =>
                  setForm((f) => ({ ...f, thickness: e.target.value }))
                }
              />
            </div>
          </div>

          {/* عدد القطع + المساحة */}
          <div className="bf-row">
            <label>Stock:</label>
            <div className="bf-grid-2">
              <label className="ui-field">{stockCountLabel(form.product_type)}
              <input
                className="bf-input"
                type="number"
                min={isEdit ? 0 : 0.001}
                step="0.001"
                aria-label={stockCountLabel(form.product_type)}
                readOnly={isEdit && distribute}
                placeholder={stockCountLabel(form.product_type)}
                value={quantity}
                onChange={(e) =>
                  setForm((f) => ({ ...f, slabs_number: e.target.value }))
                }
                required
              />
              </label>
              <label className="ui-field">Calculated area (m²)
              <input
                className="bf-input"
                readOnly
                placeholder="Total area (m²)"
                value={total_area}
              />
              </label>
            </div>
          </div>

          {/* سعر المورد */}
          <div className="bf-row ui-field">
            <label htmlFor="supplier-price">Supplier price (optional):</label>
            <input
              id="supplier-price"
              className="bf-input"
              type="number"
              min="0"
              step="0.01"
              placeholder="Example: 1200"
              value={form.m_price}
              onChange={(e) =>
                setForm((f) => ({ ...f, m_price: e.target.value }))
              }
            />
          </div>

          {/* المعالجة */}
          <fieldset className="bf-sec" disabled={mixedTreatments}>
            <h4>{mixedTreatments ? "Treatment · Multiple stock finishes" : "Treatment"}</h4>
            <div className="bf-grid-2">
              {/* الوجه */}
              <div>
                <div className="bf-warn" style={{ marginBottom: 6 }}>
                  Front
                </div>
                <div className="bf-radio">
                  <label>
                    <input
                      type="radio"
                      name="face_treat"
                      checked={treatment.face === "epoxy"}
                      onChange={() =>
                        setTreatment((t) => ({ ...t, face: "epoxy" }))
                      }
                    />{" "}
                    Epoxy
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="face_treat"
                      checked={treatment.face === "resin"}
                      onChange={() =>
                        setTreatment((t) => ({ ...t, face: "resin" }))
                      }
                    />{" "}
                    Resin
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="face_treat"
                      checked={treatment.face === "none"}
                      onChange={() =>
                        setTreatment((t) => ({ ...t, face: "none" }))
                      }
                    />{" "}
                    None
                  </label>
                </div>
              </div>
              {/* الظهر */}
              <div>
                <div className="bf-warn" style={{ marginBottom: 6 }}>
                  Back
                </div>
                <div className="bf-radio">
                  <label>
                    <input
                      type="radio"
                      name="back_treat"
                      checked={treatment.back === "epoxy"}
                      onChange={() =>
                        setTreatment((t) => ({ ...t, back: "epoxy" }))
                      }
                    />{" "}
                    Epoxy
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="back_treat"
                      checked={treatment.back === "resin"}
                      onChange={() =>
                        setTreatment((t) => ({ ...t, back: "resin" }))
                      }
                    />{" "}
                    Resin
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="back_treat"
                      checked={treatment.back === "none"}
                      onChange={() =>
                        setTreatment((t) => ({ ...t, back: "none" }))
                      }
                    />{" "}
                    None
                  </label>
                </div>
              </div>
            </div>

            {/* لمعة */}
            <div style={{ marginTop: 10 }}>
              <label>
                <input
                  type="checkbox"
                  checked={treatment.polished}
                  onChange={(e) =>
                    setTreatment((t) => ({ ...t, polished: e.target.checked }))
                  }
                />{" "}
                Polished
              </label>
            </div>
          </fieldset>

          {/* Optional stock allocation */}
          <div className="bf-sec">
            <div className="ui-heading-row"><h4>Showroom stock</h4>
              {(!isEdit || (originalStock || []).length <= 1) && <button type="button" className="ui-secondary" onClick={() => {
                if (!distribute && !showroomDistributions.length) setShowroomDistributions([{showroom_id:defaultShowroom,slabs_count:quantity}]);
                if (isEdit && distribute) setForm(current => ({...current,slabs_number:quantity}));
                setDistribute(value => !value);
              }}>{distribute ? (isEdit ? "Use total quantity" : "Use assigned showroom") : "Distribute stock (optional)"}</button>}
            </div>
            {!distribute ? <label className="ui-field">Stock showroom
              <select aria-label="Stock showroom" className="bf-input" value={isEdit && originalStock?.length ? originalStock[0].showroom_id : defaultShowroom}
                disabled={!!user?.showroom_id || (isEdit && !!originalStock?.length)} onChange={event => setDefaultShowroom(event.target.value)}>
                <option value="">Select a showroom</option>{showrooms.map(row => <option key={row.id} value={row.id}>{row.name}</option>)}
              </select>
            </label> : <>
              {showroomDistributions.map((row,index) => <div key={row.id || ("new-"+index)} className="bf-dist-row">
                <label className="ui-field">Showroom<select className="bf-input" aria-label={"Showroom "+(index+1)} value={row.showroom_id} disabled={!!row.id}
                  onChange={event => handleShowroomChange(index,"showroom_id",event.target.value)}><option value="">Select a showroom</option>
                  {showrooms.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
                </select>{row.id && <span className="ui-caption">{row.face_treatment || "none"} / {row.back_treatment || "none"}{row.is_polished ? " · Polished" : ""}</span>}</label>
                <label className="ui-field">{stockCountLabel(form.product_type)}<input className="bf-input" aria-label={"Showroom quantity "+(index+1)} type="number" min={row.id ? 0 : 0.001} step="0.001"
                  value={row.slabs_count} onChange={event => handleShowroomChange(index,"slabs_count",event.target.value)} /></label>
                {!row.id && <button type="button" className="ui-secondary" onClick={() => removeShowroomDistribution(index)}>Remove</button>}
              </div>)}
              <button type="button" className="ui-secondary" onClick={addShowroomDistribution}>Add showroom</button>
            </>}
          </div>

          <div className="bf-sec">
            <h4>Notes</h4>
            <label className="ui-field" htmlFor="block-notes">Notes (optional)</label>
            <textarea id="block-notes" rows="3" style={{width:"100%",resize:"vertical"}} value={form.notes_text}
              onChange={event => setForm(current => ({...current,notes_text:event.target.value}))} />
          </div>
          <div className="ui-full-width" style={{ marginTop: 14 }}>
            <button type="submit" className="bf-save ui-primary">
              {saving ? "Saving…" : isEdit ? "Update product" : "Save product"}
            </button>
          </div>
          </fieldset>
        </form>
      </div>

      {/* ⬇️ مودال إضافة مورد/عميل */}
      {showAddCustomer && (
        <SmallAddPopup
          onSaved={(newId) => {
            fetchAll();
            setForm((f) => ({ ...f, customer_id: newId }));
            setShowAddCustomer(false);
          }}
          onClose={() => setShowAddCustomer(false)}
        />
      )}

      {/* ⬇️ مودال إضافة خامة */}
      {showAddMaterial && (
        <MaterialAddPopup
          onSaved={(newId) => {
            fetchAll();
            setForm((f) => ({ ...f, material_id: newId }));
            setShowAddMaterial(false);
          }}
          onClose={() => setShowAddMaterial(false)}
        />
      )}
    </div>
  );
}

export default BlockForm;
